// The deco registry — single source of truth for scatter props. Decos are
// CONTENT: metadata (weight/mapColor/habitat/sway/solid/radius) lives in
// content/decos/<type>.json, validated + hydrated by src/content/load.ts;
// geometry stays CODE (seeded/procedural builds in src/deco/*.ts, registered in
// src/model/coded.ts). Add a deco = a build file + a coded.ts entry + a JSON
// metadata doc + `bun run gen`.

import type { DecoSpec, Habitat } from "./define.ts";
import { loadedDecos } from "../content/load.ts";

export const DECOS: DecoSpec[] = loadedDecos;

const habitatOf = (d: DecoSpec): Habitat => d.habitat ?? "land";

// Per-habitat scatter pools, built lazily once — DECOS is static, and the pick
// runs ~12k times per scatter compute (which the studio editor now re-runs
// after every terrain/mask edit), so don't re-filter the list per call.
const pools = new Map<Habitat, DecoSpec[]>();
function poolOf(habitat: Habitat): DecoSpec[] {
  let p = pools.get(habitat);
  if (!p) pools.set(habitat, (p = DECOS.filter((d) => d.weight > 0 && habitatOf(d) === habitat)));
  return p;
}

/** A map's scatter COMPOSITION override (MapDoc.scatterPalette). */
export type ScatterPalette = { deco: string; weight: number }[];
/** One habitat's effective scatter entries: a spec + the base weight to use. */
export type WeightedPool = { spec: DecoSpec; weight: number }[];
/** The per-habitat pools for one scatter compute. */
export type ScatterTable = Record<Habitat, WeightedPool>;

/** Build the effective per-habitat pools for a compute run. With no palette
 *  it's the classic weight>0 pools (each deco's own weight); with a palette
 *  only the listed types are eligible (still habitat-sorted), weighted by the
 *  palette. Built ONCE per computeScatter, not per cell. */
export function scatterTable(palette?: ScatterPalette): ScatterTable {
  if (!palette || palette.length === 0) {
    const base = (h: Habitat): WeightedPool => poolOf(h).map((spec) => ({ spec, weight: spec.weight }));
    return { land: base("land"), shore: base("shore"), water: base("water") };
  }
  const byType = new Map(DECOS.map((d) => [d.type, d]));
  const table: ScatterTable = { land: [], shore: [], water: [] };
  for (const { deco, weight } of palette) {
    const spec = byType.get(deco);
    if (!spec || weight <= 0) continue; // unknown/zero → not scattered
    table[habitatOf(spec)].push({ spec, weight });
  }
  return table;
}

/** Pick one deco from a habitat's pool via a [0,1) roll, weighted by each
 *  entry's weight; `weightMul` (bias masks) multiplies named types on top.
 *  Returns null on an empty pool (e.g. a palette with no type for this
 *  habitat) — the classic math when the pool is the full weight>0 set. */
export function pickDeco(
  pool: WeightedPool,
  roll: number,
  weightMul?: Record<string, number>,
): DecoSpec | null {
  if (pool.length === 0) return null;
  const w = (e: WeightedPool[number]) => e.weight * (weightMul?.[e.spec.type] ?? 1);
  const total = pool.reduce((s, e) => s + w(e), 0);
  if (total <= 0) return null;
  let t = roll * total;
  for (const e of pool) {
    t -= w(e);
    if (t <= 0) return e.spec;
  }
  return pool[0]!.spec;
}
